import { useCallback } from "react";

import datadogLogger from "@lib/datadogLogger/datadogLogger";
import type { PermissionsByScope, PermissionScope, Permission } from "@lib/permissionConstants";
import { getPermissionValue, getPermissionScopeValue } from "@lib/permissionConstants";

import { useAppGroupId } from "@services/useAppGroupId";
import { useDeveloper } from "@services/useDeveloper";
import type { DeveloperWithPermissionMap } from "@services/useDeveloper/types";
import { useFeatureOn } from "@services/useFeatureOn";

import type { UseDeveloperHasPermissionProps } from "./types";

/**
 * Returns whether the current developer has a permission in the minimum scope
 *
 * The minimum scope is most important for shared permissions (permissions that are valid in multiple scopes).
 *
 * As an example, the `"admin"` permission in the `"company"` scope affords more permissions than the `"admin"` permission
 * in the `"appGroup"` scope.
 *
 * We define scope as the minimum scope required because permissions cascade down. If a user is a company admin, then
 * they have access to all permissions at all levels. If a user is an app group admin, they have access to all permissions
 * for both app group and any teams as well.
 *
 * In addition, to validate an app group permission based on any team the user is a member of having permission,
 * pass a list of permissions that should be checked as an array to the `useDeveloperHasPermission` hook. Note that this is only applicable
 * to permissions that are both app group and team permissions, excluding the `"admin"` permission.
 *
 * In some cases, we wish to check whether the user has a permission at the app group level or for any team to which the user belongs. This
 * is often used to gate display of a page for the `basicReadOnly` permission. To accomplish this, use the pseudo-scope `"appGroupOrAnyTeamInAppGroup"`
 *
 * @example
 * const hasPermission = useDeveloperHasPermission();
 * const isAccountAdmin = hasPermission("company", { permission: "admin" });
 * // note: use the `useDeveloperHasPiiPermission` hook for true pii permission checking
 * const isPiiDeveloper = hasPermission("appGroup", { permission: "viewPii" });
 * const hasExportPermissionInTeam = hasPermission("team", { permission: "exportData", teamId: "team-id-bson" });
 * // enable escape hatch by using `appGroupOrAnyTeamInAppGroup` pseudo-scope
 * const hasExportPermission = hasPermission("appGroupOrAnyTeamInAppGroup", { permission: "basicReadOnly" });
 */
export const useDeveloperHasPermission = () => {
  const { data: developer } = useDeveloper();
  const companyId = developer.company_id;
  // TODO: remove the unsafe typecast when the new permissioning feature is proven safe
  const permissionMap = (developer as DeveloperWithPermissionMap).permissionMap;
  const appGroupId = useAppGroupId();
  const featureOn = useFeatureOn();
  const logPermissionAssertionFrontendFeature = "log_permission_assertion_frontend";
  const rumPermissionAssertionFrontendFeature = "rum_permission_assertion_frontend";

  return useCallback(
    <
      Scope extends PermissionScope | "appGroupOrAnyTeamInAppGroup",
      P extends PermissionsByScope[Scope extends "appGroupOrAnyTeamInAppGroup" ? "team" : Scope][number],
    >(
      scope: Scope,
      { permission, teamId }: UseDeveloperHasPermissionProps<Scope, P>
    ): boolean => {
      const companyAppGroupId = `${companyId}-${appGroupId}`;
      const adminPermissionInfo = permissionMap.get("admin");

      // Helper function to log permission assertions
      const logPermissionAssertionFail = (
        result: boolean,
        permissionName: unknown,
        context?: Record<string, unknown>
      ) => {
        const resolvedScope: PermissionScope = scope === "appGroupOrAnyTeamInAppGroup" ? "appGroup" : scope;
        const logContext = {
          source: "react",
          permission:
            typeof permissionName === "string" ? getPermissionValue(permissionName as Permission) : permissionName,
          permission_name: permissionName,
          scope: getPermissionScopeValue(resolvedScope),
          scope_name: scope,
          territory_id: teamId,
          company_id: companyId,
          app_group_id: appGroupId,
          has_permission: result,
          ...context,
        };

        if (featureOn(logPermissionAssertionFrontendFeature)) {
          datadogLogger.info("[useDeveloperHasPermission] - permission assertion", logContext);
        }

        if (featureOn(rumPermissionAssertionFrontendFeature)) {
          datadogLogger.userAction("permission-assertion-react", logContext);
        }
      };

      if (adminPermissionInfo?.company.has(companyId)) {
        // company admin has permissions for everything
        return true;
      }
      const isAppGroupAdmin = !!adminPermissionInfo?.appGroups.has(companyAppGroupId);
      const isAdminInAnyTeam = !!adminPermissionInfo?.anyTeam.has(companyAppGroupId);

      // check for admin privileges first
      if (scope === "appGroup" || scope === "appGroupOrAnyTeamInAppGroup") {
        if (isAppGroupAdmin) {
          return true;
        }
      }

      if (scope === "team") {
        const isTeamAdmin = !!adminPermissionInfo?.teams.get(companyAppGroupId)?.has(teamId || "");
        if (isAppGroupAdmin || isTeamAdmin) {
          return true;
        }
      }
      if (scope === "appGroupOrAnyTeamInAppGroup") {
        if (isAppGroupAdmin || isAdminInAnyTeam) {
          return true;
        }
      }

      // now, check for limited user granted permissions
      const permissionInfo = permissionMap.get(permission);
      if (!permissionInfo) {
        // user is not admin and has no explicit permissions set for this permission
        logPermissionAssertionFail(false, permission, { reason: "no_explicit_permission" });
        return false;
      }
      if (scope === "company") {
        // check whether the user has explicit permission at the company level
        const result = permissionInfo.company.has(companyId);
        logPermissionAssertionFail(result, permission, { reason: "company_scope_check" });
        return result;
      }
      const hasExplicitAppGroupPermission = permissionInfo.appGroups.has(companyAppGroupId);
      if (scope === "appGroup") {
        // check whether the user has explicit permission at the app group level
        const result = hasExplicitAppGroupPermission;
        logPermissionAssertionFail(result, permission, { reason: "app_group_scope_check" });
        return result;
      }
      if (scope === "appGroupOrAnyTeamInAppGroup") {
        // check whether the user has explicit permission at the app group level, or for any team
        const hasExplicitTeamPermissionInAppGroup = !!permissionInfo.anyTeam.has(companyAppGroupId);
        const result = hasExplicitAppGroupPermission || hasExplicitTeamPermissionInAppGroup;
        logPermissionAssertionFail(result, permission, {
          reason: "app_group_or_any_team_check",
          hasExplicitAppGroupPermission,
          hasExplicitTeamPermissionInAppGroup,
        });
        return result;
      }
      if (scope === "team") {
        // check whether the user has explicit permission at the team level for a specific team
        const hasTeamPermission = !!permissionInfo.teams.get(companyAppGroupId)?.has(teamId || "");
        const result = hasExplicitAppGroupPermission || hasTeamPermission;
        logPermissionAssertionFail(result, permission, {
          reason: "team_scope_check",
          hasExplicitAppGroupPermission,
          hasTeamPermission,
        });
        return result;
      }
      logPermissionAssertionFail(false, permission, { reason: "fallthrough_denial" });
      return false;
    },
    [appGroupId, companyId, permissionMap, featureOn]
  );
};
